/**
 * webrtc-call — Llamada de audio (+ video opcional) agente↔cliente sobre la
 * misma sesión de co-browsing. La señalización (SDP/ICE) viaja como payload
 * opaco por el mismo canal WS de eco_events (mensaje `webrtc_signal`, ya
 * relay-eado por el hub) — este módulo no sabe nada de WebSocket, solo recibe
 * un `sendSignal` para emitir y expone `handleSignal` para lo que llega.
 *
 * STUN público por defecto; si eco_events tiene TURN configurado
 * (`TURN_SERVER_URL`/`TURN_SHARED_SECRET`), `fetchTurnCredentials()` trae
 * credenciales de corta duración desde `GET /turn/credentials` y se suman a
 * `iceServers` — necesario cuando agente y/o cliente están detrás de NAT
 * simétrico/firewall corporativo, donde STUN solo no alcanza para conectar.
 * Si no hay TURN configurado (503) o falla la red, se degrada a solo-STUN
 * sin romper nada — nunca bloquea la llamada.
 */

const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]

/**
 * Pide credenciales TURN a eco_events (`GET /turn/credentials`, mismo token
 * que ya se usa para el WS de cobrowsing). `wsBaseUrl` es la URL ws(s):// del
 * hub — se convierte a http(s):// para el fetch. Nunca rechaza: cualquier
 * error/503 (TURN no configurado) devuelve `[]`, o sea "seguí solo con STUN".
 */
export async function fetchTurnCredentials(wsBaseUrl: string, token: string): Promise<RTCIceServer[]> {
  try {
    const httpBase = wsBaseUrl.replace(/^ws/, 'http').replace(/\/$/, '')
    const res = await fetch(`${httpBase}/turn/credentials?token=${encodeURIComponent(token)}`)
    if (!res.ok) return []
    const data = (await res.json()) as { urls: string[]; username: string; credential: string }
    return [{ urls: data.urls, username: data.username, credential: data.credential }]
  } catch {
    return []
  }
}

export type MediaPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

/**
 * Consulta el permiso de micrófono/cámara SIN disparar el prompt del
 * navegador (a diferencia de getUserMedia, que si lo dispara) — usado para
 * decidir si mostrar guía preventiva ("te vamos a pedir permiso") o dejar
 * en paz al usuario que ya lo concedió. `navigator.permissions.query` no
 * está soportado en todos los navegadores (Safari en particular) — en ese
 * caso devuelve 'unsupported' y el llamador debe asumir que puede hacer
 * falta pedir permiso, sin bloquear nada.
 */
export async function queryMediaPermission(name: 'microphone' | 'camera'): Promise<MediaPermissionState> {
  try {
    if (!navigator.permissions?.query) return 'unsupported'
    const status = await navigator.permissions.query({ name: name })
    return status.state
  } catch {
    return 'unsupported'
  }
}

export interface WebRTCCallOptions {
  /** true = esta punta ofrece la llamada (crea la oferta inicial). */
  isInitiator: boolean
  /** Envía un payload de señalización al otro extremo (vía session.sendWebRTCSignal o equivalente). */
  sendSignal: (payload: unknown) => void
  onRemoteStream?: (stream: MediaStream) => void
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void
  /** Credenciales TURN en vuelo (ver fetchTurnCredentials) — se resuelve antes de armar el RTCPeerConnection; se dispara en paralelo con el resto de la conexión, no agrega demora perceptible. */
  extraIceServers?: Promise<RTCIceServer[]>
}

export interface WebRTCCall {
  /** Procesa un payload de señalización recibido del otro extremo. */
  handleSignal: (payload: unknown) => void
  /** Alterna mute del micrófono propio. Devuelve el nuevo estado (true = muteado). No-op (no hace nada) si nunca hubo track de audio — usar retryAudio() para conseguir uno. */
  toggleMute: () => boolean
  /** true si el getUserMedia inicial consiguió un track de audio real (permiso ya concedido). */
  hasLocalAudio: () => boolean
  /** Reintenta conseguir el micrófono (getUserMedia) y, si funciona, lo agrega
   * a la llamada ya viva (renegocia) — para cuando el permiso se negó al
   * conectar y el usuario lo concede recién al tocar "activar micrófono".
   * Devuelve false si el permiso sigue denegado/no disponible. */
  retryAudio: () => Promise<boolean>
  /** Agrega la cámara propia a la llamada ya establecida (renegocia). Rechaza si el permiso de cámara es denegado/falla — el llamador decide cómo avisarlo. */
  addVideo: () => Promise<void>
  /**
   * Pide compartir pantalla (getDisplayMedia) y agrega el track a la llamada
   * ya establecida (renegocia, mismo patrón que addVideo). Requiere gesto
   * del usuario — el navegador rechaza la llamada si no hay un click
   * reciente detrás. Rechaza (throw) si el usuario cancela el diálogo nativo
   * o deniega el permiso — el llamador decide cómo avisarlo.
   *
   * El navegador deja que el usuario elija QUÉ compartir (pestaña/ventana/
   * pantalla completa) en un diálogo nativo que este código no puede
   * forzar — solo puede SUGERIR "pantalla completa" vía el hint
   * `displaySurface: 'monitor'`. Si el usuario comparte solo la pestaña,
   * elementos que el navegador dibuja como overlay nativo (el desplegable
   * de un <select>, por ejemplo) NO se ven en el video — necesario avisar
   * al usuario que elija "Toda la pantalla".
   *
   * Devuelve la pista de video compartida — el llamador la usa para
   * detectar cuándo el usuario para de compartir (botón nativo "Dejar de
   * compartir" del navegador, vía track.onended) y para leer su resolución
   * nativa (necesaria del lado del agente para traducir clics).
   */
  addScreenShare: () => Promise<MediaStreamTrack>
  close: () => void
}

interface SignalPayload {
  sdp?: RTCSessionDescriptionInit
  candidate?: RTCIceCandidateInit
  /** Le dice al agente si el próximo track de VIDEO que va a llegar es
   * screen-share o cámara propia — el agente no puede distinguirlos por
   * getSettings().displaySurface (esa propiedad no viaja por WebRTC, es
   * local a quien captura el track) así que hace falta avisarlo explícito
   * por señalización. Ver webrtc-call.ts del lado agente. */
  trackKind?: 'screen' | 'camera'
}

export async function createWebRTCCall(opts: WebRTCCallOptions): Promise<WebRTCCall> {
  const turnServers = opts.extraIceServers ? await opts.extraIceServers : []
  const pc = new RTCPeerConnection({ iceServers: [...ICE_SERVERS, ...turnServers] })
  let localStream: MediaStream | null = null
  let hasLocalAudio = false

  // Mic arranca SIEMPRE apagado (mismo criterio que la cámara, que solo se
  // agrega vía addVideo() cuando el cliente lo pide explícitamente) — el
  // agente pide permiso de palabra, no se asume que el cliente quiere
  // transmitir audio apenas conecta.
  let muted = true

  // Candidatos ICE que llegan ANTES de que setRemoteDescription() termine
  // (carrera real: el otro lado puede mandar el primer candidato apenas
  // manda la oferta/respuesta, y por la latencia del relay WS puede llegar
  // acá antes de que hayamos terminado de procesar el SDP). Sin este buffer,
  // pc.addIceCandidate() se llama con remoteDescription todavía null, tira
  // (capturado y descartado en silencio más abajo) y esos candidatos se
  // pierden para siempre — ICE puede quedar sin suficientes candidatos para
  // completar la conexión, o completarla con peor conectividad (sin el
  // candidato que hubiera dado la mejor ruta). Se vacía en cuanto
  // setRemoteDescription() resuelve (ver flushPendingCandidates).
  const pendingCandidates: RTCIceCandidateInit[] = []

  // Tracks remotos por id (Map, no array) — `ontrack` puede repetirse para
  // el MISMO track durante una renegociación (ej. al llamar addVideo(), que
  // dispara un nuevo createOffer()/answer sobre la conexión ya viva). Con un
  // array simple (`push` sin chequear duplicados) cada renegociación volvía
  // a agregar el track de audio ya existente al MediaStream reconstruido, y
  // el navegador terminaba reproduciendo la MISMA pista de audio superpuesta
  // varias veces — se escuchaba como un pitido/eco que "se hacía infinito"
  // a medida que se acumulaban copias. Dedupe por `track.id` + limpieza en
  // `onended` evita que la lista crezca sin límite.
  const remoteTracksMap = new Map<string, MediaStreamTrack>()

  const emitRemoteStream = () => {
    const activeTracks = Array.from(remoteTracksMap.values()).filter(
      (t) => t.readyState !== 'ended',
    )
    if (activeTracks.length > 0) {
      opts.onRemoteStream?.(new MediaStream(activeTracks))
    }
  }

  pc.onicecandidate = (e) => {
    if (e.candidate) opts.sendSignal({ candidate: e.candidate.toJSON() })
  }
  pc.ontrack = (e) => {
    const track = e.track
    remoteTracksMap.set(track.id, track)
    emitRemoteStream()
    track.onended = () => {
      remoteTracksMap.delete(track.id)
      emitRemoteStream()
    }
    track.onunmute = () => { emitRemoteStream(); }
    track.onmute = () => { emitRemoteStream(); }
  }
  if (opts.onConnectionStateChange) {
    pc.onconnectionstatechange = () => opts.onConnectionStateChange?.(pc.connectionState)
  }

  try {
    // echoCancellation/noiseSuppression explícitos — refuerzan la cancelación
    // de eco del navegador (por defecto ya suele venir activada, pero
    // dejarla implícita depende del navegador/driver de audio del equipo).
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
    localStream.getTracks().forEach((t) => {
      t.enabled = false
      pc.addTrack(t, localStream!)
    })
    hasLocalAudio = true
  } catch {
    // Sin micrófono / permiso denegado — la sesión sigue, solo sin audio
    // propio (el otro lado puede seguir escuchando si su mic sí funcionó).
    // El usuario puede reintentar más tarde con retryAudio() (ver
    // toggleMute() en use-cobrowse-support.ts).
  }

  if (opts.isInitiator) {
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    opts.sendSignal({ sdp: pc.localDescription })
  }

  const flushPendingCandidates = async () => {
    while (pendingCandidates.length > 0) {
      const candidate = pendingCandidates.shift()
      if (candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate))
        } catch {
          // Ignorar candidato con error
        }
      }
    }
  }

  async function handleSignal(payload: unknown): Promise<void> {
    const msg = payload as SignalPayload
    if (msg.sdp) {
      await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp))
      await flushPendingCandidates()
      if (msg.sdp.type === 'offer') {
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        opts.sendSignal({ sdp: pc.localDescription })
      }
    } else if (msg.candidate) {
      if (!pc.remoteDescription?.type) {
        pendingCandidates.push(msg.candidate)
      } else {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(msg.candidate))
        } catch {
          // Candidato tardío o conexión ya cerrada — ignorar.
        }
      }
    }
  }

  function toggleMute(): boolean {
    if (!hasLocalAudio) return muted // sin track — no hay nada que alternar, ver retryAudio()
    muted = !muted
    localStream?.getAudioTracks().forEach((t) => {
      t.enabled = !muted
    })
    return muted
  }

  async function retryAudio(): Promise<boolean> {
    if (hasLocalAudio) return true
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      localStream = stream
      stream.getTracks().forEach((t) => {
        t.enabled = true // el usuario acaba de pedir esto explícitamente — arranca activo, no muteado
        pc.addTrack(t, stream)
      })
      hasLocalAudio = true
      muted = false
      // Agregar un track a una conexión ya viva exige renegociar — mismo
      // patrón que addVideo() de acá abajo.
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      opts.sendSignal({ sdp: pc.localDescription })
      return true
    } catch {
      return false
    }
  }

  async function addVideo(): Promise<void> {
    const videoStream = await navigator.mediaDevices.getUserMedia({ video: true })
    videoStream.getTracks().forEach((t) => pc.addTrack(t, videoStream))
    // Quien agrega el track dispara la renegociación — el otro lado responde
    // con una respuesta ante CUALQUIER oferta entrante (ver handleSignal),
    // sin importar quién fue el "isInitiator" original.
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    opts.sendSignal({ sdp: pc.localDescription, trackKind: 'camera' })
  }

  async function addScreenShare(): Promise<MediaStreamTrack> {
    // displaySurface:'monitor' es solo un HINT para preseleccionar "Toda la
    // pantalla" en el diálogo nativo — el usuario puede igual elegir
    // pestaña/ventana, el navegador nunca deja forzarlo desde código.
    const screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'monitor' } as MediaTrackConstraints,
      audio: false,
    })
    const track = screenStream.getVideoTracks()[0]
    if (!track) throw new Error('getDisplayMedia no devolvió ninguna pista de video')
    pc.addTrack(track, screenStream)
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    opts.sendSignal({ sdp: pc.localDescription, trackKind: 'screen' })
    return track
  }

  function close(): void {
    localStream?.getTracks().forEach((t) => { t.stop(); })
    pc.close()
  }

  return { handleSignal, toggleMute, hasLocalAudio: () => hasLocalAudio, retryAudio, addVideo, addScreenShare, close }
}
